import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FIRM } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    matter: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a brief description.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        matter: form.matter.trim(),
        message: form.message.trim(),
      });
      toast.success(
        "Thank you. Your inquiry has been received. Dick will be in touch shortly."
      );
      setForm({ name: "", email: "", phone: "", matter: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Unable to submit your inquiry right now. Please call (214) 415-1759.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-cream"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        {/* Left column - Contact info */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <div className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
              Get in Touch
            </span>
          </div>

          <h2
            data-testid="contact-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight"
          >
            Schedule a
            <br />
            <span className="italic text-gold">consultation.</span>
          </h2>

          <p className="mt-8 text-base lg:text-lg text-navy/75 leading-relaxed max-w-lg">
            If your matter turns on financial issues — fraud, damages,
            bankruptcy recovery, or business dispute — Dick will listen,
            assess, and tell you straight whether he can help.
          </p>

          <div className="mt-12 space-y-6">
            <div
              data-testid="contact-phone-block"
              className="flex items-start gap-5"
            >
              <div className="w-10 h-10 rounded-sm bg-navy/5 flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-navy" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-navy/60 mb-1">
                  Call Direct
                </div>
                <a
                  href={`tel:${FIRM.phone.replace(/[^\d+]/g, "")}`}
                  className="font-serif text-xl text-navy hover:text-gold transition-colors"
                >
                  {FIRM.phone}
                </a>
                <div className="text-sm text-navy/60 mt-1">
                  Secondary: {FIRM.phoneSecondary}
                </div>
              </div>
            </div>

            <div
              data-testid="contact-email-block"
              className="flex items-start gap-5"
            >
              <div className="w-10 h-10 rounded-sm bg-navy/5 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-navy" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-navy/60 mb-1">
                  Email
                </div>
                <a
                  href={`mailto:${FIRM.email}`}
                  className="font-serif text-xl text-navy hover:text-gold transition-colors break-all"
                >
                  {FIRM.email}
                </a>
              </div>
            </div>

            <div
              data-testid="contact-address-block"
              className="flex items-start gap-5"
            >
              <div className="w-10 h-10 rounded-sm bg-navy/5 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-navy" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-navy/60 mb-1">
                  Office
                </div>
                <div className="font-serif text-lg text-navy leading-tight">
                  Richard L. Bufkin, CPA
                </div>
                <div className="text-sm text-navy/70 mt-1">
                  {FIRM.address.line1}
                  <br />
                  {FIRM.address.line2}
                </div>
              </div>
            </div>

            <div
              data-testid="contact-hours-block"
              className="flex items-start gap-5"
            >
              <div className="w-10 h-10 rounded-sm bg-navy/5 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-navy" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-navy/60 mb-1">
                  Hours
                </div>
                <div className="font-serif text-lg text-navy leading-tight">
                  {FIRM.hours}
                </div>
                <div className="text-sm text-navy/60 mt-1 italic">
                  {FIRM.hoursNote}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="bg-white border border-[#C0974E]/20 p-8 lg:p-12 shadow-xl shadow-navy/5"
          >
            <h3 className="font-serif text-2xl text-navy mb-2">
              Tell us about your matter
            </h3>
            <p className="text-sm text-navy/60 mb-8">
              All communications are confidential.
            </p>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="name"
                  className="block text-xs uppercase tracking-[0.2em] text-navy/70 mb-2 font-medium"
                >
                  Full Name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  data-testid="form-name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-navy/20 focus:border-gold focus:outline-none transition-colors text-navy font-serif text-lg"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs uppercase tracking-[0.2em] text-navy/70 mb-2 font-medium"
                >
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="form-email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-navy/20 focus:border-gold focus:outline-none transition-colors text-navy font-serif text-lg"
                  placeholder="jane@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-xs uppercase tracking-[0.2em] text-navy/70 mb-2 font-medium"
                >
                  Phone
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  data-testid="form-phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-navy/20 focus:border-gold focus:outline-none transition-colors text-navy font-serif text-lg"
                  placeholder="(555) 555-5555"
                />
              </div>
              <div>
                <label
                  htmlFor="matter"
                  className="block text-xs uppercase tracking-[0.2em] text-navy/70 mb-2 font-medium"
                >
                  Nature of Matter
                </label>
                <select
                  id="matter"
                  name="matter"
                  data-testid="form-matter"
                  value={form.matter}
                  onChange={handleChange}
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-navy/20 focus:border-gold focus:outline-none transition-colors text-navy font-serif text-lg cursor-pointer"
                >
                  <option value="">Select an area</option>
                  <option>Business Litigation</option>
                  <option>Forensic Accounting / Expert Witness</option>
                  <option>Bankruptcy / Trustee Matter</option>
                  <option>Fraud / Economic Damages</option>
                  <option>Estate, Probate or Transactional</option>
                  <option>Other</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label
                htmlFor="message"
                className="block text-xs uppercase tracking-[0.2em] text-navy/70 mb-2 font-medium"
              >
                Brief Description *
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                data-testid="form-message"
                value={form.message}
                onChange={handleChange}
                className="w-full px-0 py-3 bg-transparent border-0 border-b border-navy/20 focus:border-gold focus:outline-none transition-colors text-navy font-serif text-base resize-none"
                placeholder="A few sentences about your matter..."
              />
            </div>

            <button
              type="submit"
              data-testid="form-submit"
              disabled={submitting}
              className="btn-primary mt-10 inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.25em] uppercase font-medium group disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  Sending
                  <Loader2 className="w-4 h-4 animate-spin" />
                </>
              ) : (
                <>
                  Send Inquiry
                  <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>

            <p className="mt-6 text-xs text-navy/50 leading-relaxed">
              Submitting this form does not create an attorney–client
              relationship. Please do not include confidential information
              until such a relationship is established.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};
